<script>
  import { onDestroy, onMount } from 'svelte';
  import { useNavigate } from 'svelte-navigator';
  import debounce from 'lodash/debounce';
  import isEqual from 'lodash/isEqual';
  import noop from 'lodash/noop';

  import { DataTable, InlineNotification, Link, Tag, Toolbar, ToolbarContent, TooltipDefinition } from 'carbon-components-svelte';

  import CustomLicenseFeaturesCell from '../../components/licenses/CustomLicenseFeaturesCell.svelte';
  import LicenseStatusCell from '../../components/licenses/LicenseStatusCell.svelte';
  import LoadingSpinner from '../../components/LoadingSpinner.svelte';
  import PaginationWithRouting from '../../components/PaginationWithRouting.svelte';
  import ToolbarSearchWithRouting from '../../components/ToolbarSearchWithRouting.svelte';

  import { getLicenseTemplates } from '../../services';
  import { findLicenseStatus } from '../../utils/licenseStatus';
  import { parseSortEvent } from '../../utils';

  import { SUPPORTED_RENEWAL_CYCLES } from '../../components/licenses/LicenseTemplateBasicForm.svelte';

  const navigate = useNavigate();

  let loading = true,
    runningTasks = 0,
    licenseTemplates = [],
    licenseTemplatesCount = 0,
    searchTextInitialized = false,
    paginationInitialized = false,
    resultNotification;

  const criteria = {
    searchText: '',
    currentPage: 1,
    pageSize: 25,
    orderBy: null,
    orderDirection: null,
  };
  let prevCriteria = {};

  async function fetchLicenseTemplates({ searchText, pageSize, currentPage, orderBy, orderDirection }) {
    runningTasks++;

    try {
      const { items, count } = await getLicenseTemplates({
        search: searchText || undefined,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        orderBy: orderBy || undefined,
        orderDirection: orderDirection || undefined,
      });

      licenseTemplates = items.map((licenseTemplate) => ({
        ...licenseTemplate,
        status: findLicenseStatus(licenseTemplate, licenseTemplate?.licenseOwner?.licenseLookbackDays ?? 15),
      }));
      licenseTemplatesCount = count;
    } catch (error) {
      console.error('[LicenseTemplatesList] Failed to get license templates...', error);
      resultNotification = {
        hideCloseButton: false,
        kind: 'error',
        title: 'Error:',
        subtitle: 'Failed to get license templates! Please try again later.',
      };
    } finally {
      runningTasks--;
    }
  }

  const fetchLicenseTemplatesDebounced = debounce(fetchLicenseTemplates, 300);

  function onSearchTextInit() {
    searchTextInitialized = true;
  }

  function onPaginationInit() {
    paginationInitialized = true;
  }

  function onSearchTextChange() {
    if (criteria.currentPage !== 1) {
      criteria.currentPage = 1;
    }
  }

  const onSortChange = (event) =>
    parseSortEvent(event, ({ orderBy, orderDirection }) => {
      criteria.orderBy = orderBy;
      criteria.orderDirection = orderDirection;
    });

  function generateNavigationFunction(groupId, licenseTemplateId) {
    return function go(event) {
      event.preventDefault();
      navigate(`/group/${groupId}?template=${licenseTemplateId}#licenses`);
    };
  }

  onMount(() => {
    fetchLicenseTemplates(criteria);
    prevCriteria = { ...criteria };
  });

  onDestroy(() => fetchLicenseTemplatesDebounced.cancel());

  $: if (searchTextInitialized && paginationInitialized && !isEqual(criteria, prevCriteria)) {
    const initialized = Object.keys(prevCriteria).length > 0;
    const searchChanged = prevCriteria.searchText !== criteria.searchText;
    const clearedSearch = prevCriteria.searchText && !criteria.searchText;

    if (!initialized || !searchChanged) {
      fetchLicenseTemplates(criteria);
    } else {
      if (clearedSearch) {
        loading = true;
      }
      fetchLicenseTemplatesDebounced(criteria);
    }

    prevCriteria = { ...criteria };
  }

  $: loading = runningTasks > 0;
  $: showTable = loading || !!criteria.searchText || !!licenseTemplates.length;
</script>

<LoadingSpinner {loading}>
  {#if resultNotification}
    <InlineNotification
      kind={resultNotification.kind}
      lowContrast
      title={resultNotification.title}
      subtitle={resultNotification.subtitle}
    />
  {/if}
  {#if showTable}
    <div class="table">
      <DataTable
        headers={[
          { key: 'licenseOwner.name', value: 'Group Name', sort: noop },
          { key: 'name', value: 'License Name', sort: noop },
          {
            key: 'newestGeneratedLicense',
            value: 'Expiration Date',
            display: (data) => (data?.isPermanent ? 'No expiration' : data?.expirationDate ?? '—'),
            sort: false,
          },
          { key: 'cycle', value: 'Cycle', display: (cycle) => SUPPORTED_RENEWAL_CYCLES[cycle], sort: noop },
          { key: 'features', value: 'Features', sort: false },
          { key: 'status', value: 'Status', sort: false },
        ]}
        rows={licenseTemplates}
        sortable
        on:click:header={onSortChange}
      >
        <div slot="cell" let:cell let:row>
          {#if cell.key === 'name'}
            <div>
              {#if row.isDemo}
                <Tag type="blue" size="sm">DEMO</Tag>
              {/if}
              {#if row.licenseOwner.deletedAt}
                <TooltipDefinition tooltipText="Deleted group" portalTooltip>{row.name}</TooltipDefinition>
              {:else}
                <Link href={`/group/${row.ownerId}?template=${row.id}#licenses`} on:click={generateNavigationFunction(row.ownerId, row.id)}>
                  <strong>{row.name}</strong>
                </Link>
              {/if}
            </div>
          {:else if cell.key === 'features'}
            <CustomLicenseFeaturesCell features={row.fullFeatureList} />
          {:else if cell.key === 'status'}
            <LicenseStatusCell licenseStatus={row.status} />
          {:else if cell.display}
            {cell.display(cell.value, row)}
          {:else}
            {cell.value}
          {/if}
        </div>
        <Toolbar>
          <ToolbarContent>
            <ToolbarSearchWithRouting
              persistent
              placeholder="Search templates..."
              bind:searchText={criteria.searchText}
              on:toolbarSearchValueChange={onSearchTextChange}
              on:toolbarSearchValueInit={onSearchTextInit}
            />
          </ToolbarContent>
        </Toolbar>
      </DataTable>
      <PaginationWithRouting
        bind:page={criteria.currentPage}
        bind:pageSize={criteria.pageSize}
        pageSizes={[10, 25, 50]}
        totalItems={licenseTemplatesCount}
        on:init={onPaginationInit}
      />
    </div>
  {:else}
    <InlineNotification lowContrast hideCloseButton kind="info" title="Info:" subtitle="No license templates found" />
  {/if}
</LoadingSpinner>
